"use client";

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { clientApi } from "@/lib/api/client";
import { ENDPOINTS } from "@/constants/api/endpoints";
import { queryKeys } from "@/lib/react-query/query-keys";
import { isApiError } from "@/types/api";
import { PdpPriceCalendar } from "@/types/pdp-prices";
import { getPdpCalendarWindow } from "@/utils/pdp/calendar-window";

export interface UsePdpPriceCalendarParams {
  accommodationId: string;
  capacity: number;
}

/**
 * Available-night allowlist for the PDP datepicker.
 *
 * The range is the full selectable window (today → min(two Jalali months,
 * 62 days)), so month paging does not need a refetch. Capacity is part of
 * the key — guest count changes which nights origins can serve.
 */
export function usePdpPriceCalendar({
  accommodationId,
  capacity,
}: UsePdpPriceCalendarParams) {
  const { fromString, toString } = useMemo(() => getPdpCalendarWindow(), []);

  const query = useQuery({
    queryKey: queryKeys.accommodations.pdpPriceCalendar(
      accommodationId,
      fromString,
      toString,
      capacity,
    ),
    queryFn: async () => {
      const response = await clientApi.get<PdpPriceCalendar>(
        ENDPOINTS.ACCOMMODATIONS.PRICES_CALENDAR(
          accommodationId,
          fromString,
          toString,
          capacity,
        ),
        { showErrorToast: false },
      );

      if (isApiError(response)) {
        throw new Error(response.error?.message || "Failed to load calendar");
      }

      return unwrapCalendar(response.data);
    },
    enabled: Boolean(accommodationId && capacity >= 1),
    placeholderData: (previous) => previous,
  });

  // After a fetch, always pass an array so the picker stays in allowlist mode
  // (undefined would fail-open and leave reserved days clickable).
  // Widen `data` — Query's pending/error discriminant types it as `never` here.
  const calendarData = query.data as PdpPriceCalendar | undefined;
  const availableDates = query.isError
    ? []
    : query.isSuccess
      ? (calendarData?.availableDates ?? [])
      : calendarData?.availableDates;

  return {
    ...query,
    availableDates,
    isAvailabilityPending: query.isPending && !query.data,
  };
}

function unwrapCalendar(
  payload: PdpPriceCalendar | { data?: PdpPriceCalendar } | undefined,
): PdpPriceCalendar {
  if (payload && Array.isArray((payload as PdpPriceCalendar).availableDates)) {
    return payload as PdpPriceCalendar;
  }
  const nested = (payload as { data?: PdpPriceCalendar } | undefined)?.data;
  if (nested && Array.isArray(nested.availableDates)) {
    return nested;
  }
  throw new Error("Invalid calendar payload");
}
